/**
 * 单段 profile 曲线偏置原语。
 *
 * 设计说明：
 * 1. 负责 line / arc / spline 在未拼接前的“平行偏置”；
 * 2. 段间交点裁剪由 `joinOffsetProfileCurveSegments` 统一处理；
 * 3. 保持纯函数，供组合轮廓偏置链路复用。
 */

import type { PlanarLoopOffsetDirection, PlanarLoopOffsetPlane } from './offsetPlanarLoop';
import {
  add2,
  arePoints2Coincident,
  computeSignedArea2,
  cross2,
  normalizeVector2,
  offsetLoopCorner2,
  PLANAR_LOOP_EPSILON,
  projectPointToPlane,
  resolveOffsetNormal2,
  scale2,
  subtract2,
  unprojectPointFromPlane,
  type Point2,
} from './planarLoopProjection';

type Point3 = [number, number, number];

/** 组合轮廓辅助点列采样基准段数，与解析偏置路径保持一致。 */
export const PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS = 96;
/** 完整周期角判定容差。 */
const FULL_CYCLE_ANGLE_EPS = 1e-6;

/**
 * 组合轮廓点列采样选项。
 */
export interface SampleCompositeProfileCurveLoopOptions {
  /** 期望最少采样点数；不足时沿闭合折线补点，通常取原始 outer 点列长度。 */
  minPointCount?: number;
  /** 圆弧/完整周期基准采样段数。 */
  curveSampleSegments?: number;
}

/** 圆弧局部 2D 轴。 */
export interface ArcLocalAxes2 {
  /** 圆心。 */
  center2: Point2;
  /** 局部 X 轴。 */
  xAxis2: Point2;
  /** 局部 Y 轴。 */
  yAxis2: Point2;
}

/**
 * 归一化三维向量。
 *
 * @param value 输入向量。
 * @param fallback 退化回退。
 * @returns 单位向量。
 */
function normalizePoint3(value: Point3, fallback: Point3): Point3 {
  const length = Math.hypot(value[0], value[1], value[2]);
  if (length <= PLANAR_LOOP_EPSILON) {
    return [...fallback];
  }
  return [value[0] / length, value[1] / length, value[2] / length];
}

/**
 * 计算圆弧段局部 Y 轴（三维）。
 *
 * @param segment 圆弧段。
 * @returns 局部 Y 轴。
 */
function resolveArcYAxis3(segment: Extract<ProfileCurveSegment, { kind: 'arc' }>): Point3 {
  return normalizePoint3(
    [
      segment.normal[1] * segment.xAxis[2] - segment.normal[2] * segment.xAxis[1],
      segment.normal[2] * segment.xAxis[0] - segment.normal[0] * segment.xAxis[2],
      segment.normal[0] * segment.xAxis[1] - segment.normal[1] * segment.xAxis[0],
    ],
    [0, 1, 0],
  );
}

/**
 * 按圆弧参数角计算三维点。
 *
 * @param segment 圆弧段。
 * @param angle 参数角。
 * @returns 三维点。
 */
export function buildArcPointAtAngle3(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  angle: number,
): Point3 {
  const yAxis = resolveArcYAxis3(segment);
  const cosValue = Math.cos(angle);
  const sinValue = Math.sin(angle);
  return [
    segment.center[0] + (segment.xAxis[0] * cosValue + yAxis[0] * sinValue) * segment.radius,
    segment.center[1] + (segment.xAxis[1] * cosValue + yAxis[1] * sinValue) * segment.radius,
    segment.center[2] + (segment.xAxis[2] * cosValue + yAxis[2] * sinValue) * segment.radius,
  ];
}

/**
 * 解析圆弧局部 2D 坐标轴。
 *
 * @param segment 圆弧段。
 * @param plane 偏置平面。
 * @returns 局部 2D 轴。
 */
export function resolveArcLocalAxes2(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  plane: PlanarLoopOffsetPlane,
): ArcLocalAxes2 {
  const center2 = projectPointToPlane(segment.center, plane);
  const xAxis3 = normalizePoint3(segment.xAxis, [1, 0, 0]);
  const yAxis3 = resolveArcYAxis3(segment);
  const xAxis2Raw = projectPointToPlane(
    [plane.origin[0] + xAxis3[0], plane.origin[1] + xAxis3[1], plane.origin[2] + xAxis3[2]],
    plane,
  );
  const yAxis2Raw = projectPointToPlane(
    [plane.origin[0] + yAxis3[0], plane.origin[1] + yAxis3[1], plane.origin[2] + yAxis3[2]],
    plane,
  );
  return {
    center2,
    xAxis2: normalizeVector2(xAxis2Raw),
    yAxis2: normalizeVector2(yAxis2Raw),
  };
}

/**
 * 读取曲线段起点（三维）。
 *
 * @param segment 曲线段。
 * @returns 起点。
 * @throws 当曲线段类型不支持读取起点时抛出错误。
 */
export function getProfileCurveSegmentStart3(segment: ProfileCurveSegment): Point3 {
  if (segment.kind === 'line') {
    return [...segment.start];
  }
  if (segment.kind === 'arc') {
    return buildArcPointAtAngle3(segment, segment.startAngle);
  }
  if (segment.kind === 'circle') {
    return buildCirclePointAtAngle3(segment, 0);
  }
  if (segment.kind === 'ellipse') {
    return buildEllipsePointAtAngle3(segment, segment.startAngle);
  }
  if (segment.kind === 'spline') {
    const points = segment.curvePoints.length >= 2 ? segment.curvePoints : segment.controlPoints;
    return [...points[0]!];
  }
  throw new Error('当前曲线段不支持读取起点。');
}

/**
 * 读取曲线段终点（三维）。
 *
 * @param segment 曲线段。
 * @returns 终点。
 * @throws 当曲线段类型不支持读取终点时抛出错误。
 */
export function getProfileCurveSegmentEnd3(segment: ProfileCurveSegment): Point3 {
  if (segment.kind === 'line') {
    return [...segment.end];
  }
  if (segment.kind === 'arc') {
    return buildArcPointAtAngle3(segment, segment.endAngle);
  }
  if (segment.kind === 'circle') {
    return buildCirclePointAtAngle3(segment, 0);
  }
  if (segment.kind === 'ellipse') {
    return buildEllipsePointAtAngle3(segment, segment.endAngle);
  }
  if (segment.kind === 'spline') {
    const points = segment.curvePoints.length >= 2 ? segment.curvePoints : segment.controlPoints;
    return [...points[points.length - 1]!];
  }
  throw new Error('当前曲线段不支持读取终点。');
}

/**
 * 根据 2D 点更新圆弧起止角。
 *
 * @param segment 圆弧段。
 * @param start2 起点 2D。
 * @param end2 终点 2D。
 * @param plane 偏置平面。
 * @returns 更新后的圆弧段。
 */
export function updateArcSegmentEndpointsFromPoints2(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  start2: Point2,
  end2: Point2,
  plane: PlanarLoopOffsetPlane,
): Extract<ProfileCurveSegment, { kind: 'arc' }> {
  const axes = resolveArcLocalAxes2(segment, plane);
  const resolveAngle = (point2: Point2): number => {
    const relative = subtract2(point2, axes.center2);
    return Math.atan2(
      dot2OnAxes(relative, axes.yAxis2),
      dot2OnAxes(relative, axes.xAxis2),
    );
  };
  return {
    ...segment,
    startAngle: resolveAngle(start2),
    endAngle: resolveAngle(end2),
  };
}

/**
 * 计算二维向量在局部轴上的投影。
 *
 * @param vector 向量。
 * @param axis 轴。
 * @returns 投影标量。
 */
function dot2OnAxes(vector: Point2, axis: Point2): number {
  return vector[0] * axis[0] + vector[1] * axis[1];
}

/**
 * 解析圆弧段中点参数角。
 *
 * @param segment 圆弧段。
 * @returns 中点角。
 */
function resolveArcMidAngle2(segment: Extract<ProfileCurveSegment, { kind: 'arc' }>): number {
  const fullTurn = Math.PI * 2;
  if (segment.clockwise) {
    let sweep = segment.startAngle - segment.endAngle;
    while (sweep <= 0) {
      sweep += fullTurn;
    }
    return segment.startAngle - (sweep / 2);
  }
  let sweep = segment.endAngle - segment.startAngle;
  while (sweep <= 0) {
    sweep += fullTurn;
  }
  return segment.startAngle + (sweep / 2);
}

/**
 * 解析圆弧在轮廓偏置中的带符号半径增量。
 *
 * 设计说明：
 * 1. CCW 外环上，圆心位于行进切向左侧为凸弧（outward → R + d），右侧为凹弧（outward → R - d）；
 * 2. CW 外环取反，保证 inward/outward 始终相对轮廓内外侧而非固定加减半径；
 * 3. 直线段偏置与圆弧偏置由此保持同一套内外语义。
 *
 * @param segment 圆弧段。
 * @param plane 偏置平面。
 * @param signedArea 环符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 半径增量。
 */
export function resolveArcOffsetRadiusDelta(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  plane: PlanarLoopOffsetPlane,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): number {
  const axes = resolveArcLocalAxes2(segment, plane);
  const midAngle = resolveArcMidAngle2(segment);
  const cosValue = Math.cos(midAngle);
  const sinValue = Math.sin(midAngle);
  const midPoint2: Point2 = [
    axes.center2[0] + (cosValue * axes.xAxis2[0] + sinValue * axes.yAxis2[0]) * segment.radius,
    axes.center2[1] + (cosValue * axes.xAxis2[1] + sinValue * axes.yAxis2[1]) * segment.radius,
  ];

  // 按参数角增加方向求切向，再按实际行进方向（clockwise）修正。
  let tangent2: Point2 = [
    -sinValue * axes.xAxis2[0] + cosValue * axes.yAxis2[0],
    -sinValue * axes.xAxis2[1] + cosValue * axes.yAxis2[1],
  ];
  if (segment.clockwise) {
    tangent2 = scale2(tangent2, -1);
  }
  const travelTangent = normalizeVector2(tangent2);
  const toCenter = subtract2(axes.center2, midPoint2);
  const centerSide = cross2(travelTangent, toCenter);
  const windingSign = signedArea >= 0 ? 1 : -1;
  const convexitySign = Math.abs(centerSide) <= PLANAR_LOOP_EPSILON
    ? 1
    : Math.sign(centerSide) * windingSign;
  const signedDistance = direction === 'outward' ? distance : -distance;
  return convexitySign * signedDistance;
}

/**
 * 偏置直线段（平行平移）。
 *
 * @param segment 直线段。
 * @param plane 偏置平面。
 * @param signedArea 环符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的直线段。
 */
export function offsetLineProfileCurveSegment(
  segment: Extract<ProfileCurveSegment, { kind: 'line' }>,
  plane: PlanarLoopOffsetPlane,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): Extract<ProfileCurveSegment, { kind: 'line' }> {
  const start2 = projectPointToPlane(segment.start, plane);
  const end2 = projectPointToPlane(segment.end, plane);
  const edgeDirection = normalizeVector2(subtract2(end2, start2));
  const offsetNormal = resolveOffsetNormal2(edgeDirection, signedArea, direction);
  const delta = scale2(offsetNormal, distance);
  return {
    kind: 'line',
    start: unprojectPointFromPlane(add2(start2, delta), plane),
    end: unprojectPointFromPlane(add2(end2, delta), plane),
  };
}

/**
 * 偏置圆弧段（按局部凸凹性调整半径）。
 *
 * @param segment 圆弧段。
 * @param plane 偏置平面。
 * @param signedArea 环符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的圆弧段。
 * @throws 当偏置后的圆弧半径不大于轮廓容差时抛出错误。
 */
export function offsetArcProfileCurveSegment(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  plane: PlanarLoopOffsetPlane,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): Extract<ProfileCurveSegment, { kind: 'arc' }> {
  const radiusDelta = resolveArcOffsetRadiusDelta(segment, plane, signedArea, direction, distance);
  const nextRadius = segment.radius + radiusDelta;
  if (nextRadius <= PLANAR_LOOP_EPSILON) {
    throw new Error('薄壁偏置后圆弧半径过小，无法生成有效轮廓。');
  }
  return {
    ...segment,
    radius: nextRadius,
  };
}

/**
 * 计算两三维点间的欧几里得距离。
 *
 * @param left 点 A。
 * @param right 点 B。
 * @returns 距离。
 */
function distance3(left: Point3, right: Point3): number {
  return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]);
}

/**
 * 使用 Catmull-Rom 插值对样条点列做自适应加密。
 *
 * 设计说明：
 * 1. 原始 curvePoints 在曲率大的区域可能过于稀疏，直接做逐点法向偏置会产生扭曲；
 * 2. Catmull-Rom 插值可保证插值点通过所有原始点且 C1 连续，保留原始样条形状；
 * 3. 边界处理：首段用 P0,P0,P1,P2 复制首点，末段用 Pn-3,Pn-2,Pn-1,Pn-1 复制末点；
 * 4. 加密阈值按偏置距离自适应：maxSegmentLength = |distance| * 0.5。
 *
 * @param points 原始点列。
 * @param maxSegmentLength 最大允许段长；小于等于 0 时不加密。
 * @returns 加密后的点列（首尾点保持原始位置）。
 */
function densifySplineCurvePoints(points: Point3[], maxSegmentLength: number): Point3[] {
  if (points.length < 2 || maxSegmentLength <= 0) {
    return points.map((p) => [...p] as Point3);
  }

  const result: Point3[] = [];
  const n = points.length;
  const tension = 0.5;

  for (let i = 0; i < n - 1; i += 1) {
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const chordLength = distance3(p1, p2);

    // 段首加入 p1
    if (i === 0) {
      result.push([...p1]);
    }

    // 段长已满足精度要求，直接保留 p2
    if (chordLength <= maxSegmentLength) {
      result.push([...p2]);
      continue;
    }

    // Catmull-Rom 切线计算：P0 = i-1, P3 = i+2；边界时延展
    const p0 = i > 0 ? points[i - 1]! : p1;
    const p3 = i + 2 < n ? points[i + 2]! : p2;

    const tangent1: Point3 = [
      tension * (p2[0] - p0[0]),
      tension * (p2[1] - p0[1]),
      tension * (p2[2] - p0[2]),
    ];
    const tangent2: Point3 = [
      tension * (p3[0] - p1[0]),
      tension * (p3[1] - p1[1]),
      tension * (p3[2] - p1[2]),
    ];

    // 按段长决定细分段数：保证加密后段长 ≤ maxSegmentLength
    const subdivisions = Math.ceil(chordLength / maxSegmentLength);

    for (let s = 1; s <= subdivisions; s += 1) {
      const t = s / subdivisions;
      const tt = t * t;
      const ttt = tt * t;
      // Hermite 基函数
      const h1 = 2 * ttt - 3 * tt + 1;
      const h2 = ttt - 2 * tt + t;
      const h3 = -2 * ttt + 3 * tt;
      const h4 = ttt - tt;
      result.push([
        h1 * p1[0] + h2 * tangent1[0] + h3 * p2[0] + h4 * tangent2[0],
        h1 * p1[1] + h2 * tangent1[1] + h3 * p2[1] + h4 * tangent2[1],
        h1 * p1[2] + h2 * tangent1[2] + h3 * p2[2] + h4 * tangent2[2],
      ]);
    }
  }

  return result;
}

/**
 * 对密集点列做自适应下采样，使最小点间距不小于目标值。
 *
 * @param points 原始点列。
 * @param minSpacing 最小允许间距。
 * @returns 下采样后的点列（首尾点保持原始位置）。
 */
function downsamplePoints(points: Point3[], minSpacing: number): Point3[] {
  if (points.length < 3) {
    return points.map((p) => [...p] as Point3);
  }

  const result: Point3[] = [[...points[0]!] as Point3];
  for (let index = 1; index < points.length - 1; index += 1) {
    const lastPoint = result[result.length - 1]!;
    const currentPoint = points[index]!;
    if (Math.hypot(
      currentPoint[0] - lastPoint[0],
      currentPoint[1] - lastPoint[1],
      currentPoint[2] - lastPoint[2],
    ) >= minSpacing) {
      result.push([...currentPoint] as Point3);
    }
  }
  result.push([...points[points.length - 1]!] as Point3);
  return result;
}

/**
 * 偏置开放样条段（弦线交点法）。
 *
 * 设计说明：
 * 1. 先对稀疏样条点做 Catmull-Rom 自适应加密，使折线精确拟合原始样条形状；
 * 2. 内部点使用弦线交点法（chord-edge intersection）偏置，保持边平行性，
 *    避免密集点列因相邻法向差异产生微回环/自交；
 * 3. 端点保持逐点法向偏置，将在 `joinOffsetProfileCurveSegments` 中被修正到接缝交点。
 *
 * @param segment 样条段。
 * @param plane 偏置平面。
 * @param signedArea 环符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的样条段。
 * @throws 当样条段没有至少两个可用点时抛出错误。
 */
export function offsetSplineProfileCurveSegment(
  segment: Extract<ProfileCurveSegment, { kind: 'spline' }>,
  plane: PlanarLoopOffsetPlane,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): Extract<ProfileCurveSegment, { kind: 'spline' }> {
  const sourcePoints = segment.curvePoints.length >= 2 ? segment.curvePoints : segment.controlPoints;
  if (sourcePoints.length < 2) {
    throw new Error('样条段点数不足，无法执行偏置。');
  }

  // Step 1: 自适应下采样 → 加密，使弦长与偏置距离匹配
  //         过密点列（弦长 << distance）会导致 chord-edge 求交代数退化，
  //         下采样到弦长 ≥ |distance| × 2 后，chord-edge 求交稳定
  const targetSpacing = Math.max(Math.abs(distance) * 2, 1e-5);
  const sampledPoints = downsamplePoints(sourcePoints, targetSpacing);
  const maxSegmentLength = Math.max(Math.abs(distance) * 0.5, 1e-6);
  const densifiedPoints = densifySplineCurvePoints(sampledPoints, maxSegmentLength);

  // Step 2: 投影到二维平面
  const localPoints = densifiedPoints.map((point) => projectPointToPlane(point, plane));

  // Step 3: 统一使用弦线交点法（下采样后弦长足够，双向均稳定）
  const offsetLocalPoints = localPoints.map((current, index) => {
    const pointCount = localPoints.length;
    if (index <= 0 || index >= pointCount - 1) {
      const neighbor = index <= 0 ? localPoints[1]! : localPoints[pointCount - 2]!;
      const tangent = index <= 0
        ? normalizeVector2(subtract2(neighbor, current))
        : normalizeVector2(subtract2(current, neighbor));
      const offsetNormal = resolveOffsetNormal2(tangent, signedArea, direction);
      return add2(current, scale2(offsetNormal, distance));
    }
    return offsetLoopCorner2(
      localPoints[index - 1]!,
      current,
      localPoints[index + 1]!,
      signedArea,
      direction,
      distance,
    );
  });

  // Step 4: 反投影回三维空间
  const offsetCurvePoints = offsetLocalPoints.map((point) => unprojectPointFromPlane(point, plane));
  return {
    ...segment,
    curvePoints: offsetCurvePoints,
    controlPoints: segment.controlPoints.length >= 2
      ? offsetCurvePoints
      : segment.controlPoints,
  };
}

/**
 * 对单段 profile 曲线执行未拼接偏置。
 *
 * @param segment 原始曲线段。
 * @param plane 偏置平面。
 * @param signedArea 环符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的曲线段。
 * @throws 当曲线段不是受支持的直线、圆弧或样条时抛出错误。
 */
export function offsetRawProfileCurveSegment(
  segment: ProfileCurveSegment,
  plane: PlanarLoopOffsetPlane,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): ProfileCurveSegment {
  if (segment.kind === 'line') {
    return offsetLineProfileCurveSegment(segment, plane, signedArea, direction, distance);
  }
  if (segment.kind === 'arc') {
    return offsetArcProfileCurveSegment(segment, plane, signedArea, direction, distance);
  }
  if (segment.kind === 'spline') {
    return offsetSplineProfileCurveSegment(segment, plane, signedArea, direction, distance);
  }
  throw new Error('当前曲线段不支持组合偏置。');
}

/**
 * 校验曲线段环是否首尾闭合。
 *
 * @param segments 曲线段列表。
 * @returns 是否闭合。
 */
export function areProfileCurveSegmentsClosed(segments: ProfileCurveSegment[]): boolean {
  if (segments.length <= 0) {
    return false;
  }
  for (let index = 0; index < segments.length; index += 1) {
    const currentEnd = getProfileCurveSegmentEnd3(segments[index]!);
    const nextStart = getProfileCurveSegmentStart3(segments[(index + 1) % segments.length]!);
    if (!arePoints3Coincident(currentEnd, nextStart)) {
      return false;
    }
  }
  return true;
}

/**
 * 判断两个三维点是否近似重合。
 *
 * @param left 点 A。
 * @param right 点 B。
 * @param tolerance 容差。
 * @returns 是否重合。
 */
export function arePoints3Coincident(
  left: Point3,
  right: Point3,
  tolerance = PLANAR_LOOP_EPSILON,
): boolean {
  return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]) <= tolerance;
}

/**
 * 根据曲线段环计算符号面积。
 *
 * @param segments 曲线段。
 * @param plane 偏置平面。
 * @returns 符号面积。
 */
export function computeProfileCurveLoopSignedArea2(
  segments: ProfileCurveSegment[],
  plane: PlanarLoopOffsetPlane,
): number {
  // 段起点共线时 shoelace 面积为 0，会误判内外侧；需沿完整边界采样。
  const sampledRing = sampleProfileCurveLoopPointsFromSegments(segments)
    .map((point) => projectPointToPlane(point, plane));
  if (sampledRing.length >= 3) {
    const sampledArea = computeSignedArea2(sampledRing);
    if (Math.abs(sampledArea) > PLANAR_LOOP_EPSILON) {
      return sampledArea;
    }
  }

  const startRing = segments.map((segment) => (
    projectPointToPlane(getProfileCurveSegmentStart3(segment), plane)
  ));
  return computeSignedArea2(startRing);
}

/**
 * 沿闭合折线补点，使采样密度不低于期望下限。
 *
 * @param points 原始采样点列（首尾不必重复）。
 * @param minPointCount 期望最少点数。
 * @returns 补点后的点列。
 */
function densifyClosedPointLoop(points: Point3[], minPointCount: number): Point3[] {
  if (points.length >= minPointCount || points.length < 2) {
    return points;
  }

  const densified = points.map((point) => [...point] as Point3);
  while (densified.length < minPointCount) {
    let longestEdgeIndex = 0;
    let longestEdgeLength = -1;
    for (let index = 0; index < densified.length; index += 1) {
      const start = densified[index]!;
      const end = densified[(index + 1) % densified.length]!;
      const edgeLength = Math.hypot(end[0] - start[0], end[1] - start[1], end[2] - start[2]);
      if (edgeLength > longestEdgeLength) {
        longestEdgeLength = edgeLength;
        longestEdgeIndex = index;
      }
    }

    const start = densified[longestEdgeIndex]!;
    const end = densified[(longestEdgeIndex + 1) % densified.length]!;
    densified.splice(longestEdgeIndex + 1, 0, [
      (start[0] + end[0]) * 0.5,
      (start[1] + end[1]) * 0.5,
      (start[2] + end[2]) * 0.5,
    ]);
  }

  return densified;
}

/**
 * 采样组合曲线环点列。
 *
 * 设计说明：
 * 1. 建模以 `segments` 为准，本函数生成与之对应的辅助折线点列；
 * 2. 圆弧采样密度与解析偏置路径一致，避免 outer 被过度稀疏化；
 * 3. 可通过 `minPointCount` 对齐原始 outer 采样密度。
 *
 * @param segments 曲线段列表。
 * @param options 采样选项。
 * @returns 采样点列。
 */
export function sampleCompositeProfileCurveLoopPoints(
  segments: ProfileCurveSegment[],
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point3[] {
  const curveSampleSegments = options.curveSampleSegments ?? PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS;
  const points: Point3[] = [];
  segments.forEach((segment, index) => {
    if (segment.kind === 'line') {
      if (index === 0 || !arePoints3Coincident(points[points.length - 1]!, segment.start)) {
        points.push([...segment.start]);
      }
      points.push([...segment.end]);
      return;
    }
    if (segment.kind === 'arc') {
      const sampleCount = Math.max(8, Math.ceil(
        curveSampleSegments * (resolveArcSweepRadians(segment) / (Math.PI * 2)),
      ));
      resolveArcSampleAngles(segment, sampleCount).forEach((angle) => {
        const point = buildArcPointAtAngle3(segment, angle);
        if (points.length <= 0 || !arePoints3Coincident(points[points.length - 1]!, point)) {
          points.push(point);
        }
      });
      return;
    }
    if (segment.kind === 'spline') {
      const curvePoints = segment.curvePoints.length >= 2 ? segment.curvePoints : segment.controlPoints;
      curvePoints.forEach((point, pointIndex) => {
        if (pointIndex === 0 && points.length > 0 && arePoints3Coincident(points[points.length - 1]!, point)) {
          return;
        }
        points.push([...point]);
      });
    }
  });

  if (options.minPointCount && options.minPointCount > points.length) {
    return densifyClosedPointLoop(points, options.minPointCount);
  }
  return points;
}

/**
 * 解析圆弧实际扫掠弧度（含顺逆时针语义）。
 *
 * @param segment 圆弧段。
 * @returns 扫掠弧度。
 */
function resolveArcSweepRadians(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
): number {
  const fullTurn = Math.PI * 2;
  let normalizedEnd = segment.endAngle;
  if (segment.clockwise) {
    while (normalizedEnd >= segment.startAngle) {
      normalizedEnd -= fullTurn;
    }
  } else {
    while (normalizedEnd <= segment.startAngle) {
      normalizedEnd += fullTurn;
    }
  }
  return Math.abs(normalizedEnd - segment.startAngle);
}

/**
 * 按 segment 起止角与顺逆时针生成圆弧采样角列表。
 *
 * @param segment 圆弧段。
 * @param minCount 最少分段数。
 * @returns 采样角列表。
 */
function resolveArcSampleAngles(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  minCount: number,
): number[] {
  const fullTurn = Math.PI * 2;
  let normalizedEnd = segment.endAngle;
  if (segment.clockwise) {
    while (normalizedEnd >= segment.startAngle) {
      normalizedEnd -= fullTurn;
    }
  } else {
    while (normalizedEnd <= segment.startAngle) {
      normalizedEnd += fullTurn;
    }
  }
  const count = Math.max(minCount, 8);
  return Array.from({ length: count + 1 }, (_, index) => (
    segment.startAngle + ((normalizedEnd - segment.startAngle) * index) / count
  ));
}

/**
 * 按 segment 语义采样圆弧点列（与校验/Puppy CAD 内核 辅助折线一致）。
 *
 * @param segment 圆弧段。
 * @param curveSampleSegments 整圆参考分段数。
 * @returns 圆弧采样点列。
 */
export function sampleProfileArcSegmentPoints(
  segment: Extract<ProfileCurveSegment, { kind: 'arc' }>,
  curveSampleSegments = PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS,
): Point3[] {
  const sampleCount = Math.max(8, Math.ceil(
    curveSampleSegments * (resolveArcSweepRadians(segment) / (Math.PI * 2)),
  ));
  return resolveArcSampleAngles(segment, sampleCount).map((angle) => buildArcPointAtAngle3(segment, angle));
}

/**
 * 判断椭圆段是否表示完整闭合椭圆。
 *
 * @param segment 椭圆曲线段。
 * @returns 是否为完整周期椭圆。
 */
export function isFullProfileEllipseSegment(
  segment: Extract<ProfileCurveSegment, { kind: 'ellipse' }>,
): boolean {
  const angleSpan = Math.abs(segment.endAngle - segment.startAngle);
  const normalizedSpan = angleSpan % (Math.PI * 2);
  return normalizedSpan < FULL_CYCLE_ANGLE_EPS
    || Math.abs(normalizedSpan - Math.PI * 2) < FULL_CYCLE_ANGLE_EPS;
}

/**
 * 判断单段解析曲线是否可走解析偏置/采样。
 *
 * @param segments 曲线段列表。
 * @returns 是否支持。
 */
export function canPreserveAnalyticProfileCurveSegments(segments: ProfileCurveSegment[]): boolean {
  if (segments.length !== 1) {
    return false;
  }
  const segment = segments[0]!;
  if (segment.kind === 'circle' || segment.kind === 'arc') {
    return true;
  }
  if (segment.kind === 'ellipse') {
    return isFullProfileEllipseSegment(segment);
  }
  return false;
}

/**
 * 规范化周期角范围，保证结束角大于起始角。
 *
 * @param start 起始角。
 * @param end 结束角。
 * @returns 规范化后的角范围。
 */
function normalizePeriodicRange(start: number, end: number): [number, number] {
  let normalizedEnd = end;
  const fullTurn = Math.PI * 2;
  while (normalizedEnd <= start) {
    normalizedEnd += fullTurn;
  }
  return [start, normalizedEnd];
}

/**
 * 按圆参数角计算三维点。
 *
 * @param segment 圆曲线段。
 * @param angle 参数角。
 * @returns 三维点。
 */
function buildCirclePointAtAngle3(
  segment: Extract<ProfileCurveSegment, { kind: 'circle' }>,
  angle: number,
): Point3 {
  const yAxis = resolveArcYAxis3({
    kind: 'arc',
    center: segment.center,
    radius: segment.radius,
    startAngle: 0,
    endAngle: 0,
    clockwise: false,
    normal: segment.normal,
    xAxis: segment.xAxis,
  });
  const cosValue = Math.cos(angle);
  const sinValue = Math.sin(angle);
  return [
    segment.center[0] + (segment.xAxis[0] * cosValue + yAxis[0] * sinValue) * segment.radius,
    segment.center[1] + (segment.xAxis[1] * cosValue + yAxis[1] * sinValue) * segment.radius,
    segment.center[2] + (segment.xAxis[2] * cosValue + yAxis[2] * sinValue) * segment.radius,
  ];
}

/**
 * 按椭圆参数角计算三维点。
 *
 * @param segment 椭圆曲线段。
 * @param angle 参数角。
 * @returns 三维点。
 */
export function buildEllipsePointAtAngle3(
  segment: Extract<ProfileCurveSegment, { kind: 'ellipse' }>,
  angle: number,
): Point3 {
  const cosRotation = Math.cos(segment.rotation);
  const sinRotation = Math.sin(segment.rotation);
  const localX = Math.cos(angle) * segment.radiusX;
  const localY = Math.sin(angle) * segment.radiusY;
  const rotatedX = localX * cosRotation - localY * sinRotation;
  const rotatedY = localX * sinRotation + localY * cosRotation;
  return [
    segment.center[0] + segment.xAxis[0] * rotatedX + segment.yAxis[0] * rotatedY,
    segment.center[1] + segment.xAxis[1] * rotatedX + segment.yAxis[1] * rotatedY,
    segment.center[2] + segment.xAxis[2] * rotatedX + segment.yAxis[2] * rotatedY,
  ];
}

/**
 * 采样整圆点列。
 *
 * @param segment 圆曲线段。
 * @param curveSampleSegments 整圆参考分段数。
 * @returns 闭合采样点。
 */
export function sampleProfileCircleSegmentPoints(
  segment: Extract<ProfileCurveSegment, { kind: 'circle' }>,
  curveSampleSegments = PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS,
): Point3[] {
  return Array.from({ length: curveSampleSegments }, (_, index) => {
    const angle = (index / curveSampleSegments) * Math.PI * 2;
    return buildCirclePointAtAngle3(segment, angle);
  });
}

/**
 * 采样椭圆点列。
 *
 * @param segment 椭圆曲线段。
 * @param curveSampleSegments 整圆参考分段数。
 * @returns 椭圆采样点。
 */
export function sampleProfileEllipseSegmentPoints(
  segment: Extract<ProfileCurveSegment, { kind: 'ellipse' }>,
  curveSampleSegments = PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS,
): Point3[] {
  const cosRotation = Math.cos(segment.rotation);
  const sinRotation = Math.sin(segment.rotation);
  const [startAngle, endAngle] = segment.clockwise
    ? normalizePeriodicRange(segment.endAngle, segment.startAngle)
    : normalizePeriodicRange(segment.startAngle, segment.endAngle);
  const sampleCount = Math.max(8, Math.ceil(
    curveSampleSegments * ((endAngle - startAngle) / (Math.PI * 2)),
  ));

  return Array.from({ length: sampleCount + 1 }, (_, index) => {
    const ratio = index / sampleCount;
    const angle = segment.clockwise
      ? startAngle - (startAngle - endAngle) * ratio
      : startAngle + (endAngle - startAngle) * ratio;
    const localX = Math.cos(angle) * segment.radiusX;
    const localY = Math.sin(angle) * segment.radiusY;
    const rotatedX = localX * cosRotation - localY * sinRotation;
    const rotatedY = localX * sinRotation + localY * cosRotation;
    return [
      segment.center[0] + segment.xAxis[0] * rotatedX + segment.yAxis[0] * rotatedY,
      segment.center[1] + segment.xAxis[1] * rotatedX + segment.yAxis[1] * rotatedY,
      segment.center[2] + segment.xAxis[2] * rotatedX + segment.yAxis[2] * rotatedY,
    ];
  });
}

/**
 * 采样单段解析曲线点列。
 *
 * @param segment 解析曲线段。
 * @param options 采样选项。
 * @returns 采样点列。
 * @throws 当曲线段不是受支持的圆、圆弧或椭圆时抛出错误。
 */
export function sampleAnalyticProfileCurveSegmentPoints(
  segment: ProfileCurveSegment,
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point3[] {
  const curveSampleSegments = options.curveSampleSegments ?? PROFILE_COMPOSITE_CURVE_SAMPLE_SEGMENTS;
  if (segment.kind === 'circle') {
    return sampleProfileCircleSegmentPoints(segment, curveSampleSegments);
  }
  if (segment.kind === 'arc') {
    return sampleProfileArcSegmentPoints(segment, curveSampleSegments);
  }
  if (segment.kind === 'ellipse') {
    return sampleProfileEllipseSegmentPoints(segment, curveSampleSegments);
  }
  throw new Error('当前曲线段不支持解析采样。');
}

/**
 * 采样单段 profile 曲线点列。
 *
 * @param segment 曲线段。
 * @param options 采样选项。
 * @returns 采样点列。
 */
export function sampleProfileCurveSegmentPoints(
  segment: ProfileCurveSegment,
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point3[] {
  if (segment.kind === 'line') {
    return [[...segment.start], [...segment.end]];
  }
  if (segment.kind === 'arc' || segment.kind === 'circle' || segment.kind === 'ellipse') {
    return sampleAnalyticProfileCurveSegmentPoints(segment, options);
  }
  if (segment.kind === 'spline') {
    const curvePoints = segment.curvePoints.length >= 2 ? segment.curvePoints : segment.controlPoints;
    return curvePoints.map((point) => [...point] as Point3);
  }
  return [];
}

/**
 * 区域环采样输入。
 */
export interface ProfileRegionRingSampleInput {
  /** 闭合折线点列。 */
  points: Point3[];
  /** 精确曲线段。 */
  segments?: ProfileCurveSegment[];
}

/**
 * 判断三维点是否有限。
 *
 * @param point 三维点。
 * @returns 是否有限。
 */
function isFinitePoint3(point: Point3): boolean {
  return Number.isFinite(point[0]) && Number.isFinite(point[1]) && Number.isFinite(point[2]);
}

/**
 * 根据曲线段列表采样闭合点环。
 *
 * 设计说明：
 * 1. 单段圆/椭圆/圆弧优先走解析采样，与偏置链路保持一致；
 * 2. line/arc 组合轮廓走组合采样；
 * 3. 其余段逐段采样拼接。
 *
 * @param segments 曲线段列表。
 * @param options 采样选项。
 * @returns 采样点列。
 */
export function sampleProfileCurveLoopPointsFromSegments(
  segments: ProfileCurveSegment[],
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point3[] {
  if (segments.length <= 0) {
    return [];
  }
  if (canPreserveAnalyticProfileCurveSegments(segments)) {
    return sampleAnalyticProfileCurveSegmentPoints(segments[0]!, options);
  }
  if (canOffsetCompositeProfileCurveLoop(segments)) {
    return sampleCompositeProfileCurveLoopPoints(segments, options);
  }

  const points: Point3[] = [];
  segments.forEach((segment, segmentIndex) => {
    const sampledPoints = sampleProfileCurveSegmentPoints(segment, options);
    sampledPoints.forEach((point, pointIndex) => {
      if (segmentIndex > 0 && pointIndex === 0) {
        return;
      }
      points.push(point);
    });
  });
  return points;
}

/**
 * 解析区域环三维点列。
 *
 * 设计说明：
 * 1. 单段解析曲线与 line/arc 组合段优先按 segments 采样，保证与 Puppy CAD 内核 构面语义一致；
 * 2. 样条组合或采样退化时回退到 outer 折线点列；
 * 3. 偏置、校验与 profile 构建共用同一入口，避免椭圆等曲线误报面积退化。
 *
 * @param input 区域环输入。
 * @param options 采样选项。
 * @returns 三维闭环点列。
 */
export function sampleProfileRegionRingPoints3(
  input: ProfileRegionRingSampleInput,
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point3[] {
  const validPoints = input.points.filter(isFinitePoint3);
  const segments = input.segments;

  if (segments && segments.length > 0) {
    const sampledPoints = sampleProfileCurveLoopPointsFromSegments(segments, {
      ...options,
      minPointCount: options.minPointCount ?? (validPoints.length >= 3 ? validPoints.length : undefined),
    });
    if (sampledPoints.length >= 3) {
      return sampledPoints;
    }
  }

  if (validPoints.length >= 3) {
    return validPoints.map((point) => [...point] as Point3);
  }

  if (segments && segments.length > 0) {
    return sampleProfileCurveLoopPointsFromSegments(segments, options);
  }

  return validPoints.map((point) => [...point] as Point3);
}

/**
 * 解析区域环二维点列。
 *
 * @param input 区域环输入。
 * @param plane 投影平面。
 * @param options 采样选项。
 * @returns 二维闭环点列。
 */
export function resolveProfileRegionRingPoints2(
  input: ProfileRegionRingSampleInput,
  plane: PlanarLoopOffsetPlane,
  options: SampleCompositeProfileCurveLoopOptions = {},
): Point2[] {
  return sampleProfileRegionRingPoints3(input, options).map((point) => projectPointToPlane(point, plane));
}

/**
 * 判断曲线段列表是否可走组合偏置。
 *
 * @param segments 曲线段列表。
 * @returns 是否支持。
 */
export function canOffsetCompositeProfileCurveLoop(segments: ProfileCurveSegment[]): boolean {
  if (segments.length < 2) {
    return false;
  }
  if (!segments.every((segment) => segment.kind === 'line' || segment.kind === 'arc' || segment.kind === 'spline')) {
    return false;
  }
  /**
   * 组合轮廓的段数 ≥ 2，其中 spline 段必然是开放边界段，
   * 其 isClosed 标志仅对独立闭合样条有效，不影响组合偏置可行性。
   * 是否真正闭合由 areProfileCurveSegmentsClosed 统一校验。
   */
  return areProfileCurveSegmentsClosed(segments);
}
